import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Layout } from './components/Layout';
import { Dashboard } from './pages/Dashboard';

// Everything but the landing page loads on demand, so first visits only
// download the home page (the internal /project dashboard alone is ~2k lines).
const named = <K extends string>(load: () => Promise<Record<K, React.ComponentType>>, key: K) =>
  lazy(() => load().then((m) => ({ default: m[key] })));
const HowItWorks = named(() => import('./pages/HowItWorks'), 'HowItWorks');
const Tips = named(() => import('./pages/Tips'), 'Tips');
const InspectWizard = named(() => import('./pages/InspectWizard'), 'InspectWizard');
const Inspections = named(() => import('./pages/Inspections'), 'Inspections');
const InspectionDetail = named(() => import('./pages/InspectionDetail'), 'InspectionDetail');
const API = named(() => import('./pages/API'), 'API');
const TermsOfService = named(() => import('./pages/TermsOfService'), 'TermsOfService');
const PrivacyPolicy = named(() => import('./pages/PrivacyPolicy'), 'PrivacyPolicy');
const Project = named(() => import('./pages/Project'), 'Project');
const Article = named(() => import('./pages/Article'), 'Article');
const Admin = named(() => import('./pages/Admin'), 'Admin');
const RoadTrip = named(() => import('./pages/RoadTrip'), 'RoadTrip');

/**
 * The /project and /dashboard routes are internal dashboards — they own their
 * full-width chrome, so they shouldn't render inside the public-site Layout wrapper.
 */
function AppShell() {
  const location = useLocation();
  const isProject = /^\/(project|dashboard)/.test(location.pathname);

  const routes = (
    <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>
    <Routes>
      <Route path="/" element={<Dashboard />} />
      <Route path="/how-it-works" element={<HowItWorks />} />
      <Route path="/tips" element={<Tips />} />
      <Route path="/road-trip" element={<RoadTrip />} />
      <Route path="/tips/:slug" element={<Article />} />
      <Route path="/inspect" element={<InspectWizard />} />
      <Route path="/inspections" element={<Inspections />} />
      <Route path="/inspections/:id" element={<InspectionDetail />} />
      <Route path="/api" element={<API />} />
      <Route path="/terms" element={<TermsOfService />} />
      <Route path="/privacy" element={<PrivacyPolicy />} />
      <Route path="/project" element={<Project />} />
      <Route path="/dashboard" element={<Admin />} />
      <Route path="/dashbaord" element={<Navigate to="/dashboard" replace />} />
    </Routes>
    </Suspense>
  );

  return isProject ? routes : <Layout>{routes}</Layout>;
}

function App() {
  return (
    <BrowserRouter>
      <AppShell />
    </BrowserRouter>
  );
}

export default App;
